import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendEmailVerification,
  signInWithPopup
} from 'firebase/auth';
import { doc, setDoc, getDoc, collection, query, where, getDocs, updateDoc, serverTimestamp, onSnapshot, arrayUnion } from 'firebase/firestore';
import { auth, db, googleProvider } from '../firebase';
import { logAuthError } from '../utils/authErrorHandler';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null); // legacy single role string
  const [userCategory, setUserCategory] = useState(null); // 'admin' | 'employee' | 'client'
  const [accountStatus, setAccountStatus] = useState(null); // 'active' | 'pending' | 'suspended'
  const [rolesArray, setRolesArray] = useState([]); // string[]
  const [allowedPages, setAllowedPages] = useState([]); // string[]
  const [userPhotoURL, setUserPhotoURL] = useState(null); // Profile photo URL
  const [loading, setLoading] = useState(true);

  // Register a new user
  async function register(email, password, displayName, role, invitationCode = null) {
    try {
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const uid = userCredential.user.uid;

      // If employee, validate invitation code BEFORE creating profile
      if (role === 'employee') {
        if (!invitationCode) {
          await userCredential.user.delete();
          throw new Error("Invitation code is required for employees.");
        }

        // Check code in Firestore
        // Note: Using the newly created auth user's permissions
        const q = query(collection(db, 'invitation_codes'), where('code', '==', invitationCode), where('status', '==', 'active'));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
          // Rollback: Delete the auth user if code is invalid
          await userCredential.user.delete();
          throw new Error("Invalid or expired invitation code.");
        }

        const codeDoc = querySnapshot.docs[0];
        const codeData = codeDoc.data();
        const maxUses = codeData.maxUses || 1;
        const currentUses = codeData.usedCount || 0;

        // Double-check limit logic (though 'active' status should cover it, race conditions happen)
        if (currentUses >= maxUses) {
          await userCredential.user.delete();
          throw new Error("This invitation code has reached its usage limit.");
        }

        // Increment usage
        const newUses = currentUses + 1;
        const isFullyUsed = newUses >= maxUses;

        await updateDoc(doc(db, 'invitation_codes', codeDoc.id), {
          usedCount: newUses,
          status: isFullyUsed ? 'used' : 'active', // 'used' means fully used now
          usedBy: arrayUnion(uid),
          lastUsedAt: serverTimestamp()
        });
      }

      // Update profile with display name
      await updateProfile(userCredential.user, {
        displayName: displayName
      });

      // Map role to category:
      // - 'employee' role → 'employee' category
      // - 'client' role → 'client' category
      // - default to 'client' if not specified
      let category = 'client';
      if (role === 'employee') category = 'employee';
      else if (role === 'admin') category = 'admin';

      // Store additional user data in Firestore
      await setDoc(doc(db, 'users', uid), {
        email,
        displayName,
        role, // legacy field (keep for backward compatibility)
        category, // new field (based on selected role)
        // If code was valid (passed above), we can set them to active, 
        // OR we can still set them to pending if we want double-verification.
        // User requested: "only allowing the employees we approved".
        // The code PROVES approval. So 'active' is safe.
        // However, I will set 'active' here.
        accountStatus: 'active',
        roles: [], // new roles array (for multiple roles)
        allowedPages: [], // new pages array (for workspace access)
        createdAt: new Date().toISOString(),
        emailVerified: false
      }, { merge: true });

      // Send email verification
      await sendEmailVerification(userCredential.user);

      return userCredential.user;
    } catch (error) {
      // Log the error for debugging
      logAuthError(error, 'register-context');
      throw error;
    }
  }

  // Login user
  async function login(email, password) {
    try {
      return await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      // Log the error for debugging
      logAuthError(error, 'login-context');
      throw error;
    }
  }

  // Logout user
  async function logout() {
    try {
      if (currentUser) {
        clearUserCache(currentUser.uid);
      }
      return await signOut(auth);
    } catch (error) {
      // Log the error for debugging
      logAuthError(error, 'logout-context');
      throw error;
    }
  }

  // Sign in with Google
  async function signInWithGoogle() {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      // Check if user profile exists in Firestore
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        // New user - create profile as client (Google sign-in is client-only)
        await setDoc(userDocRef, {
          email: user.email,
          displayName: user.displayName || user.email.split('@')[0],
          photoURL: user.photoURL || null, // Save Google profile photo
          role: 'client',
          category: 'client',
          accountStatus: 'active',
          roles: [],
          allowedPages: [],
          createdAt: new Date().toISOString(),
          emailVerified: true, // Google accounts are pre-verified
          authProvider: 'google'
        }, { merge: true });
      } else {
        // Existing user - update photoURL from Google if available
        if (user.photoURL) {
          await setDoc(userDocRef, {
            photoURL: user.photoURL,
            authProvider: 'google'
          }, { merge: true });
        }
      }

      return user;
    } catch (error) {
      logAuthError(error, 'google-signin-context');
      throw error;
    }
  }

  // Resend verification email
  async function resendVerificationEmail() {
    try {
      if (currentUser && !currentUser.emailVerified) {
        await sendEmailVerification(currentUser);
        return true;
      }
      return false;
    } catch (error) {
      logAuthError(error, 'resend-verification');
      throw error;
    }
  }

  // Check verification status (refresh token)
  async function checkVerificationStatus() {
    try {
      if (currentUser) {
        await currentUser.reload();
        // Force refresh of current user state
        const refreshedUser = auth.currentUser;
        setCurrentUser({ ...refreshedUser });
        return refreshedUser.emailVerified;
      }
      return false;
    } catch (error) {
      logAuthError(error, 'check-verification');
      throw error;
    }
  }

  // Get user profile from Firestore with caching
  async function getUserProfile(uid) {
    try {
      // Check cache first
      const cacheKey = `user_profile_${uid}`;
      const cachedData = sessionStorage.getItem(cacheKey);

      if (cachedData) {
        const { data, timestamp } = JSON.parse(cachedData);
        const cacheAge = Date.now() - timestamp;
        const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

        // Return cached data if still fresh
        if (cacheAge < CACHE_TTL) {

          return data;
        }
      }

      // Cache miss or stale - fetch from Firestore

      const snap = await getDoc(doc(db, 'users', uid));
      if (!snap.exists()) return null;

      const data = snap.data();

      // Store in cache with timestamp
      sessionStorage.setItem(cacheKey, JSON.stringify({
        data,
        timestamp: Date.now()
      }));

      return data;
    } catch (error) {
      logAuthError(error, 'get-user-profile');
      return null;
    }
  }

  // Clear user profile cache
  function clearUserCache(uid) {
    if (uid) {
      sessionStorage.removeItem(`user_profile_${uid}`);
    }
  }

  // Listen for auth state changes and user profile updates
  useEffect(() => {
    let unsubscribeProfile = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      // Clean up previous profile listener if exists
      if (unsubscribeProfile) {
        unsubscribeProfile();
        unsubscribeProfile = null;
      }

      if (user) {
        // Set up real-time listener for user profile
        const userRef = doc(db, 'users', user.uid);
        unsubscribeProfile = onSnapshot(userRef, (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            setUserRole(data.role ?? null);
            setUserCategory(data.category ?? data.role ?? null);
            setAccountStatus(data.accountStatus ?? 'active');
            setRolesArray(Array.isArray(data.roles) ? data.roles : []);
            setAllowedPages(Array.isArray(data.allowedPages) ? data.allowedPages : []);
            // Set photo URL: prefer Firestore (manual uploads), fallback to Auth (Google)
            setUserPhotoURL(data.photoURL || user.photoURL || null);
          } else {
            console.warn("User profile not found for uid:", user.uid);
            setUserRole(null);
            setUserCategory(null);
            setAccountStatus(null);
            setRolesArray([]);
            setAllowedPages([]);
          }
          setLoading(false); // Initial load complete
        }, (error) => {
          console.error("Error listening to user profile:", error);
          setLoading(false); // Initial load complete even on error
        });
      } else {
        setUserRole(null);
        setUserCategory(null);
        setAccountStatus(null);
        setRolesArray([]);
        setAllowedPages([]);
        setUserPhotoURL(null);
        setLoading(false); // Initial load complete
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeProfile) unsubscribeProfile();
    };
  }, []);

  const value = {
    currentUser,
    userRole,
    userCategory,
    accountStatus,
    rolesArray,
    allowedPages,
    userPhotoURL,
    register,
    login,
    logout,
    signInWithGoogle,
    resendVerificationEmail,
    checkVerificationStatus,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
